import React, { lazy, Suspense, useEffect, useState } from 'react';

export type ClientOnlyComponent<P> = React.FC<P> & {
    /** Starts fetching the chunk; resolves once it is ready to render. */
    load: () => Promise<unknown>;
};

/**
 * React.lazy for browser-only modules. Renders nothing in the build-time HTML
 * and on the hydration pass, so markup always matches, then loads and renders
 * the component after mount.
 */
export function clientOnly<P>(
    factory: () => Promise<{ default: React.ComponentType<P> }>
): ClientOnlyComponent<P> {
    let chunk: Promise<{ default: React.ComponentType<P> }> | undefined;
    const load = () => (chunk ??= factory());
    const LazyComponent = lazy(load) as unknown as React.ComponentType<P>;

    const ClientOnly = (props: P) => {
        const [mounted, setMounted] = useState(false);
        useEffect(() => setMounted(true), []);
        if (!mounted) return null;

        return (
            <Suspense fallback={null}>
                <LazyComponent {...(props as any)} />
            </Suspense>
        );
    };

    return Object.assign(ClientOnly, { load });
}
